/**
 * SkyRenderer — physically-based Rayleigh + Mie + ozone sky node.
 *
 * Implements an inline ray-march equivalent of the Hillaire 2020 sky-view
 * LUT computation. Each fragment:
 *
 *   1. Computes the view direction from the camera through the dome.
 *   2. Intersects the ray with the atmosphere shell (top sphere).
 *   3. Marches `numSamples` steps through the shell. At each sample:
 *        - evaluates Rayleigh/Mie density
 *        - ray-marches a "sun visibility" ray from the sample to the top of
 *          the atmosphere toward the sun and accumulates optical depth
 *        - adds in-scattered radiance weighted by phase functions and
 *          cumulative view transmittance
 *   4. Returns the final summed radiance.
 *
 * Complexity is `O(numSamples * sunSamples)` per pixel. The defaults
 * (30 × 8) are a good quality / perf compromise for a fullscreen sky.
 *
 * This node is designed for `scene.backgroundNode` assignment and for
 * rendering into a small cube target (for PMREM IBL). It's not intended
 * for per-pixel use by arbitrary materials — those should read the PMREM
 * environment map or a future aerial-perspective volume.
 *
 * v1 trades the 4-LUT Hillaire pipeline for simplicity. A v2 can port the
 * LUT bakers (transmittance 256×64, multi-scatter 32×32, sky-view 192×108)
 * for ~10× fragment-shader speedup at the cost of compute-pass management.
 * Parameterization helpers for that path are already in `lutParameterization.ts`.
 *
 * Units: kilometres inside the shader; caller converts world meters → km.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, Loop, vec3, vec4, float, uniform, uint,
  exp, max, sqrt, dot, normalize,
  positionWorldDirection,
} from 'three/tsl';
import type { AtmosphereConfig } from './AtmosphereConfig.js';
import {
  atmosphereDensities,
  rayleighPhaseFn, cornetteShanksPhaseFn, rayShellIntersect,
  createSkyUniforms, updateSkyUniforms, type SkyUniforms,
} from './shaders/atmosphereFn.js';
import {
  SkyLutSet, type SkyLutUpdateResult,
  createSkyViewNodeUniforms, buildSkyViewBackgroundNode,
  type SkyViewNodeUniforms,
  transmittanceToTopOfAtmosphere,
} from './lut/index.js';

/** Which sky evaluation path to use. */
export type SkyPipeline = 'inline' | 'lut';

export interface SkyMarchParams {
  /** Primary view-ray steps. 20..40 works well. */
  numSamples?: number;
  /** Sun-visibility ray steps per main sample. 6..12 works well. */
  sunSamples?: number;
}

export interface SkyRendererOptions {
  atmosphere: AtmosphereConfig;
  march?: SkyMarchParams;
  /** Sun illuminance (cd/m² scale) applied as a constant multiplier. */
  sunIlluminance?: number;
  /**
   * Sky evaluation pipeline. 'inline' = v1 per-fragment ray-march (default,
   * zero-risk). 'lut' = v2 Hillaire 4-LUT path (samples the baked sky-view
   * LUT — ~10× cheaper per fragment, requires a CPU bake on sun/atm change).
   */
  pipeline?: SkyPipeline;
  /** Bake the aerial-perspective volume (compositing deferred). Default false. */
  enableAerialPerspective?: boolean;
}

/**
 * Holds the TSL node + mutable uniforms so the caller can push changes
 * (sun direction, atmosphere config, camera altitude) each frame without
 * rebuilding the node graph.
 */
export class SkyRenderer {
  readonly skyUniforms: SkyUniforms;
  readonly sunDirection: ReturnType<typeof uniform>;
  readonly sunIlluminance: ReturnType<typeof uniform>;
  readonly cameraAltitudeKm: ReturnType<typeof uniform>;
  readonly numSamples: number;
  readonly sunSamples: number;
  readonly pipeline: SkyPipeline;

  private _node: unknown | null = null;
  private _atmosphere: AtmosphereConfig;

  // ── v2 LUT path state (only populated when pipeline === 'lut') ──
  private _lutSet: SkyLutSet | null = null;
  private _lutNodeUniforms: SkyViewNodeUniforms | null = null;
  /** Current sun direction kept on the CPU for sun-zenith cosine derivation. */
  private _sunDir = new THREE.Vector3(0, 1, 0);
  /** Altitude the sun-disc tint was last integrated at (NaN = never). */
  private _sunTintAltitudeKm = Number.NaN;

  constructor(opts: SkyRendererOptions) {
    this.numSamples = opts.march?.numSamples ?? 30;
    this.sunSamples = opts.march?.sunSamples ?? 8;
    this.pipeline = opts.pipeline ?? 'inline';
    this._atmosphere = opts.atmosphere;

    this.skyUniforms = createSkyUniforms(opts.atmosphere);
    this.sunDirection = uniform(vec3(0, 1, 0));      // default: sun at zenith
    this.sunIlluminance = uniform(opts.sunIlluminance ?? 20.0); // unitless — user-tuned
    this.cameraAltitudeKm = uniform(opts.atmosphere.bottomRadius + 0.001);

    if (this.pipeline === 'lut') {
      this._lutSet = new SkyLutSet({
        atmosphere: opts.atmosphere,
        sunIlluminance: opts.sunIlluminance ?? 20.0,
        enableAerialPerspective: opts.enableAerialPerspective ?? false,
      });
      this._lutNodeUniforms = createSkyViewNodeUniforms({
        sunIlluminance: opts.sunIlluminance ?? 20.0,
        bottomRadius: opts.atmosphere.bottomRadius,
        viewHeightKm: opts.atmosphere.bottomRadius + 0.001,
      });
    }
  }

  /** The owned LUT set (v2 path only; null in inline mode). */
  get lutSet(): SkyLutSet | null { return this._lutSet; }

  /** The atmosphere config currently driving this renderer. */
  get atmosphere(): AtmosphereConfig { return this._atmosphere; }

  /** Push new atmosphere parameters to the GPU. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._atmosphere = cfg;
    // The sun-disc tint integrates THIS config: invalidate the early-out key so
    // the next (possibly identical) sun push re-integrates against the new one.
    this._sunTintAltitudeKm = NaN;
    updateSkyUniforms(this.skyUniforms, cfg);
    this._lutSet?.setAtmosphere(cfg);
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.bottomRadius.value = cfg.bottomRadius;
    }
  }

  /** Set the world-space unit sun direction (FROM origin TO sun). */
  setSunDirection(dir: { x: number; y: number; z: number }): void {
    const altKm = this.cameraAltitudeKm.value as number;
    // SkyRuntimeSystem (and every cloud layer) pushes the sun EVERY frame; when
    // neither the direction nor the altitude the tint was integrated at has
    // changed, every write below is a no-op — skip the ~40-sample integral.
    const unchanged = this._sunDir.x === dir.x && this._sunDir.y === dir.y
      && this._sunDir.z === dir.z && this._sunTintAltitudeKm === altKm;
    if (unchanged) return;
    (this.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
    this._sunDir.set(dir.x, dir.y, dir.z);
    if (this._lutNodeUniforms) {
      (this._lutNodeUniforms.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
      // Sun-disc tint = atmospheric transmittance toward the sun, evaluated on
      // the CPU from the SAME config the LUTs bake from (~40-sample integral
      // — only when the sun or the observer altitude actually moved). White
      // at noon, deep orange at the horizon, → 0 below it. The disc previously
      // rendered flat white at every elevation — the biggest sunset realism miss.
      const cosZenith = Math.max(-1, Math.min(1, dir.y));
      const t = transmittanceToTopOfAtmosphere(
        this._atmosphere,
        altKm,
        cosZenith,
      );
      (this._lutNodeUniforms.sunTint.value as THREE.Vector3).set(t.x, t.y, t.z);
    }
    this._sunTintAltitudeKm = altKm;
  }

  /** Set the camera altitude in kilometres from planet center. */
  setCameraAltitudeKm(radiusKm: number): void {
    this.cameraAltitudeKm.value = radiusKm;
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.viewHeightKm.value = radiusKm;
    }
  }

  /** Set the sun illuminance multiplier. */
  setSunIlluminance(v: number): void {
    this.sunIlluminance.value = v;
    this._lutSet?.setSunIlluminance(v);
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.sunIlluminance.value = v;
    }
  }

  /**
   * v2 path: regenerate dirty LUTs from the current sun + camera altitude.
   * No-op for the inline pipeline. Returns which tiers re-baked (so the
   * caller can decide whether to trigger a PMREM env re-bake), or null when
   * not in LUT mode.
   *
   * The sun-zenith cosine is derived from the world-space sun direction's
   * Y component (world +Y is "up" in the flat-frame scene, matching the
   * inline node's `up = vec3(0,1,0)` convention).
   */
  updateLuts(force = false): SkyLutUpdateResult | null {
    if (!this._lutSet) return null;
    const cosSunZenith = Math.max(-1, Math.min(1, this._sunDir.y));
    const viewHeightKm = this.cameraAltitudeKm.value as number;
    return this._lutSet.update(cosSunZenith, viewHeightKm, force);
  }

  /**
   * Build (and memoize) the TSL node that evaluates sky radiance given a
   * world-space view direction. Intended to be assigned to
   * `scene.backgroundNode` or used inside a custom skybox material.
   *
   * The view ray is `positionWorldDirection` — the fragment's direction on
   * the sky dome / background sphere, independent of camera translation
   * (a background is at infinity). Altitude comes from the
   * `cameraAltitudeKm` uniform, never from the camera's world position.
   */
  buildBackgroundNode(): unknown {
    if (this._node) return this._node;

    // ── v2 LUT path: sample the baked sky-view LUT + analytic sun disc. ──
    if (this.pipeline === 'lut' && this._lutSet && this._lutNodeUniforms) {
      // Ensure the LUTs exist before the node references the sky-view texture.
      this._lutSet.update(
        Math.max(-1, Math.min(1, this._sunDir.y)),
        this.cameraAltitudeKm.value as number,
        true,
      );
      const sv = this._lutSet.skyView;
      if (sv) {
        this._node = buildSkyViewBackgroundNode(sv.texture, this._lutNodeUniforms);
        return this._node;
      }
      // Fall through to inline if the bake somehow failed.
    }

    const u = this.skyUniforms;
    const numSamples = this.numSamples;
    const sunSamples = this.sunSamples;

    // View direction for a fragment on the background sphere.
    // WHY (the "sky slides when the player moves" bug): scene.backgroundNode is
    // drawn on a UNIT sphere with an IDENTITY matrixWorld, camera-locked via a
    // w=0 vertex transform (three common/Background.js) — so positionWorld here
    // is a unit-length direction, NOT cameraPos + dir. The old
    // normalize(positionWorld - cameraPosition) subtracted a metres-scale camera
    // position from a unit vector, swinging every sky ray as the camera
    // translated (sky black/uniform away from the origin, and the night sky
    // visibly slid against the star field while walking). A star is at
    // infinity: its ray must be translation-invariant — positionWorldDirection
    // is exactly that (and identical on the env-baker's origin-centred sphere).
    const viewDirWorld = positionWorldDirection;

    // Approximate "up" = world +Y. The planet's "up" at the camera is
    // straight up in world space for a flat-earth local scene.
    const up = vec3(0, 1, 0);

    // Camera radius from planet center (km) — user-set uniform.
    const r0 = this.cameraAltitudeKm;

    // Scattering evaluation — inline ray-march.
    const evalSkyFn = Fn(() => {
      const V = viewDirWorld;
      const L = normalize(this.sunDirection);
      const mu = dot(V, up);                 // cosViewZenith
      const muS = dot(L, up);                // cosSunZenith (at camera)
      const cosVL = dot(V, L);               // scatter angle cosine

      // Ray length through the atmosphere shell
      const tMax = rayShellIntersect([r0, mu, u.topRadius]);
      // Guard tiny values so divisions don't blow up
      const rayLen = max(tMax, float(0.01));

      const stepLen = rayLen.div(float(numSamples));
      const phaseR = rayleighPhaseFn([cosVL]);
      const phaseM = cornetteShanksPhaseFn([cosVL, u.miePhaseG]);

      // Accumulators
      const accumScatter = vec3(0, 0, 0).toVar('accumScatter');
      const accumTransmittance = vec3(1, 1, 1).toVar('accumTransmittance');

      // Main view-ray march.
      Loop({ start: uint(0), end: uint(numSamples), type: 'uint' }, ({ i }) => {
        const tMid = stepLen.mul(float(i).add(0.5));
        // Altitude at this sample, approximated linearly along the ray
        // (exact would use sqrt(r0² + 2*r0*mu*t + t²) - Rg).
        const rAtSample = sqrt(
          r0.mul(r0).add(tMid.mul(tMid)).add(r0.mul(mu).mul(tMid).mul(2.0)),
        );
        const altitudeKm = rAtSample.sub(u.bottomRadius);

        const density = atmosphereDensities([altitudeKm, u]);
        const stepExtinction = u.rayleighScattering.mul(density.x)
          .add(u.mieExtinction.mul(density.y))
          .add(u.ozoneExtinction.mul(density.z));
        const stepTransmittance = exp(stepExtinction.mul(stepLen).negate());

        // Sun-visibility ray from this sample to top of atm along -L direction.
        // Assume Sun direction — the ray from sample → top in -L direction
        // is: travel distance `tSun` within the shell. Compute µ_L at sample
        // (dot of L with local up at sample).
        const muSunAtSample = muS;   // flat approximation
        const tSun = rayShellIntersect([rAtSample, muSunAtSample, u.topRadius]);
        const sunStep = max(tSun, float(0)).div(float(sunSamples));
        const sunOpticalDepth = vec3(0, 0, 0).toVar('sunOpticalDepth');
        Loop({ start: uint(0), end: uint(sunSamples), type: 'uint' }, ({ i: j }) => {
          const tSunMid = sunStep.mul(float(j).add(0.5));
          const rSun = sqrt(
            rAtSample.mul(rAtSample)
              .add(tSunMid.mul(tSunMid))
              .add(rAtSample.mul(muSunAtSample).mul(tSunMid).mul(2.0)),
          );
          const altSun = rSun.sub(u.bottomRadius);
          const dSun = atmosphereDensities([altSun, u]);
          const extSun = u.rayleighScattering.mul(dSun.x)
            .add(u.mieExtinction.mul(dSun.y))
            .add(u.ozoneExtinction.mul(dSun.z));
          sunOpticalDepth.addAssign(extSun.mul(sunStep));
        });
        const sunTransmittance = exp(sunOpticalDepth.negate());

        // In-scattering at this sample
        const scatterR = u.rayleighScattering.mul(density.x).mul(phaseR);
        const scatterM = u.mieScattering.mul(density.y).mul(phaseM);
        const inScatter = scatterR.add(scatterM).mul(sunTransmittance);
        accumScatter.addAssign(
          inScatter.mul(accumTransmittance).mul(stepLen),
        );
        accumTransmittance.mulAssign(stepTransmittance);
      });

      const final = accumScatter.mul(this.sunIlluminance);
      return vec4(final, 1.0);
    });

    this._node = evalSkyFn();
    return this._node;
  }

  /**
   * Returns a ready-to-use THREE.NodeMaterial that evaluates the sky for
   * any fragment. Useful when rendering the sky into a cube target or a
   * sphere mesh (rather than via scene.backgroundNode).
   */
  buildSkyMaterial(): THREE.NodeMaterial {
    const material = new THREE.NodeMaterial();
    material.fragmentNode = this.buildBackgroundNode() as never;
    material.side = THREE.BackSide;
    material.depthWrite = false;
    material.depthTest = false;
    return material;
  }

  dispose(): void {
    this._node = null;
    this._lutSet?.dispose();
    this._lutSet = null;
    this._lutNodeUniforms = null;
  }
}
